import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { createPermission } from '@backstage/plugin-permission-common';

// Kept in sync with STATIC_EXTERNAL_PAGE_PERMISSION_PREFIX in
// plugins/static-external-pages-backend/src/service/router.ts and
// packages/backend/src/extensions/ElitePermissionPolicy.ts.
const STATIC_EXTERNAL_PAGE_PERMISSION_PREFIX = 'static-external-page.access.';
const SUBMENU_ANNOTATION = 'elite.io/static-external-page-submenu';
const DEFAULT_SUBMENU = 'External Pages';

export type StaticExternalPage = {
  uid: string;
  title: string;
  submenu: string;
};

export function getStaticExternalPagePermission(pageId: string) {
  return createPermission({
    name: STATIC_EXTERNAL_PAGE_PERMISSION_PREFIX + pageId,
    attributes: { action: 'read' },
  });
}

/**
 * Discovers static-external-page catalog entities, grouped by their submenu
 * annotation. Returns every candidate page regardless of the current user's
 * access -- per-page access (see ElitePermissionPolicy) is checked
 * separately via usePermission/RequirePermission at render time (see
 * ExternalPageSubmenuGroup), which revalidates on focus/reconnect via SWR.
 * Filtering here instead, with a one-shot authorize() call, would go stale
 * the moment a user's group membership changed without a full page reload.
 */
export function useStaticExternalPageGroups(): Map<string, StaticExternalPage[]> {
  const catalogApi = useApi(catalogApiRef);
  const [groups, setGroups] = useState<Map<string, StaticExternalPage[]>>(
    () => new Map(),
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'Resource', 'spec.type': 'static-external-page' },
        fields: [
          'metadata.uid',
          'metadata.name',
          'metadata.title',
          'metadata.annotations',
        ],
      });

      const next = new Map<string, StaticExternalPage[]>();
      for (const entity of items) {
        if (!entity.metadata.uid) continue;
        const page: StaticExternalPage = {
          uid: entity.metadata.uid,
          title: entity.metadata.title ?? entity.metadata.name,
          submenu:
            entity.metadata.annotations?.[SUBMENU_ANNOTATION] ??
            DEFAULT_SUBMENU,
        };
        if (!next.has(page.submenu)) next.set(page.submenu, []);
        next.get(page.submenu)!.push(page);
      }

      if (!cancelled) setGroups(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return groups;
}
